import { yupResolver } from '@hookform/resolvers/yup';
import { PaymentBy } from '@zorro/clients';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Radio, RadioGroup } from '@zorro/design-system/v2/Radio';
import { formatDateISO, parseDateISO } from '@zorro/shared/formatters';
import {
  DateRangeInput,
  FormErrorMessage,
  Group,
  Stack,
} from '@zorro/web/design-system';
import { useForm, useMonolithMutation } from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FormFooter } from '../../FormFooter';
import { InformationBoxComponent } from '../../InformationBox';
import { RecordForActions } from '../EmployeeActionsUtils';

const EmployeeLeaveOfAbsenceFormSchema = yup.object({
  leaveDates: yup
    .tuple([yup.date().required().nullable(), yup.date().required().nullable()])
    .required(),
  paymentBy: yup.string().oneOf(Object.values(PaymentBy)).required(),
});

type EmployeeLeaveOfAbsenceFormFields = yup.InferType<
  typeof EmployeeLeaveOfAbsenceFormSchema
>;

type EmployeeLeaveOfAbsenceFormProps = {
  recordsForActions: RecordForActions[];
  onSuccess?: () => void;
};

export function EmployeeLeaveOfAbsenceForm({
  recordsForActions,
  onSuccess,
}: EmployeeLeaveOfAbsenceFormProps) {
  const singleEmployee = recordsForActions[0];

  const { tryMutate: mutateSetLeaveOfAbsence } = useMonolithMutation({
    method: 'employeesControllerSetLeaveOfAbsence',
    successMessage: 'Leave of absence set successfully!',
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, errors },
  } = useForm<EmployeeLeaveOfAbsenceFormFields>({
    mode: 'all',
    resolver: yupResolver(EmployeeLeaveOfAbsenceFormSchema),
    defaultValues: {
      leaveDates:
        singleEmployee?.leaveOfAbsenceStartDate &&
        singleEmployee?.leaveOfAbsenceEndDate
          ? [
              parseDateISO(singleEmployee.leaveOfAbsenceStartDate).toDate(),
              parseDateISO(singleEmployee.leaveOfAbsenceEndDate).toDate(),
            ]
          : undefined,
      paymentBy: singleEmployee?.leaveOfAbsencePaymentBy || undefined,
    },
  });

  async function setLeaveOfAbsence(
    formFields: EmployeeLeaveOfAbsenceFormFields
  ) {
    const { leaveDates, paymentBy } = formFields;

    if (leaveDates.length !== 2 || !(leaveDates[0] && leaveDates[1])) {
      return;
    }

    const mutateResult = await mutateSetLeaveOfAbsence([
      singleEmployee.employeeId,
      {
        startDate: formatDateISO(leaveDates[0]),
        endDate: formatDateISO(leaveDates[1]),
        paymentBy,
      },
    ]);

    if (mutateResult.isOk()) {
      onSuccess?.();
    }
  }

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Submit"
        primaryButtonProps={{
          disabled: !isValid || isSubmitting,
          onClick: handleSubmit(setLeaveOfAbsence),
        }}
      />
    ),
    [handleSubmit, setLeaveOfAbsence, isValid, isSubmitting]
  );
  useDrawerFooter(drawerFooter);

  if (!singleEmployee) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit(setLeaveOfAbsence)}>
      <Stack mih="calc(100vh - 140px)" px="xxl">
        <InformationBoxComponent
          title="Here's what to expect:"
          items={[
            "After you set a leave of absence, the Zorro team will temporarily suspend the employee's allowance in the system.",
            'The employee will remain enrolled in their plan. Depending on the payment set-up during the leave, they may need to contact the carrier to replace the Zorro card with another form of payment.',
          ]}
        />

        <Stack>
          <Controller
            control={control}
            name="leaveDates"
            render={({ field }) => {
              return (
                <DateRangeInput
                  {...field}
                  required
                  label="Leave dates"
                  w="20rem"
                />
              );
            }}
          />

          <Controller
            control={control}
            name="paymentBy"
            render={({ field: { ref: _ref, ...rest } }) => (
              <RadioGroup
                {...rest}
                label="How would you like to manage premium payments during the leave?"
                required
              >
                <Group mt="xs">
                  <Radio
                    value={PaymentBy.EMPLOYEE}
                    label="Let the employee handle the payments"
                  />
                  <Radio
                    value={PaymentBy.EMPLOYER}
                    label="The employer will continue to pay and collect any owed amounts"
                  />
                </Group>
              </RadioGroup>
            )}
          />
          <FormErrorMessage errors={errors} fieldName="paymentBy" />
        </Stack>
      </Stack>
    </form>
  );
}
